import React from 'react';
import { ChevronLeft } from 'lucide-react';

const CopyrightNote = () => {
    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 via-gray-100 to-gray-200 dark:from-slate-950 dark:via-slate-950 dark:to-slate-950 flex justify-center py-8">
            <div className="w-full max-w-3xl px-4 lg:px-8">
                <button
                    onClick={() => window.location.hash = '/'}
                    className="inline-flex items-center text-gray-600 hover:text-gray-900 dark:text-slate-400 dark:hover:text-slate-200 font-medium mb-8 transition-colors"
                >
                    <ChevronLeft size={18} className="mr-1" />
                    Back to Home
                </button>

                <article className="bg-white dark:bg-slate-900 rounded-3xl shadow-xl p-8 md:p-12 border border-gray-200 dark:border-slate-800">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Copyright & Content Use</h1>

                    <div className="prose dark:prose-invert max-w-none text-gray-600 dark:text-slate-300 space-y-4">

                        <p>
                            This page explains how BrieflyCA handles news content, summarizes public stories,
                            and respects the intellectual property rights of all original publishers.
                        </p>

                        <h2 className="text-xl font-semibold text-gray-800 dark:text-slate-100 mt-6 mb-2">
                            How BrieflyCA Uses News Content
                        </h2>
                        <p>
                            BrieflyCA rewrites publicly available news headlines and stories into short,
                            easy-to-read summaries using a combination of automated systems and editorial formatting.
                            These summaries are <strong>original rewritten content</strong> and are not direct copies
                            of publisher articles.
                        </p>
                        <p>
                            The purpose of these summaries is to provide users with brief overviews of Canadian news.
                        </p>

                        <h2 className="text-xl font-semibold text-gray-800 dark:text-slate-100 mt-6 mb-2">
                            Ownership of Original Content
                        </h2>
                        <p>
                            All original articles, headlines, images, trademarks, and reporting belong to their
                            respective news publishers. BrieflyCA does not claim ownership of any third-party content.
                        </p>
                        <p>
                            Whenever possible, we credit the original publisher and encourage users to read the
                            full article on the publisher’s website for complete context.
                        </p>

                        <h2 className="text-xl font-semibold text-gray-800 dark:text-slate-100 mt-6 mb-2">
                            Fair Dealing & Fair Use Disclaimer
                        </h2>
                        <p>
                            The use of brief excerpts, headlines, and factual reporting elements is done in accordance
                            with applicable <strong>Canadian fair dealing</strong> guidelines and, where relevant,
                            <strong>U.S. fair use</strong> principles. Our summaries are transformative and intended for:
                        </p>

                        <ul className="list-disc pl-5 space-y-1">
                            <li>news reporting</li>
                            <li>review and commentary</li>
                            <li>education and public interest</li>
                            <li>informational use</li>
                        </ul>

                        <p>
                            BrieflyCA does not reproduce full articles and does not distribute copyrighted material
                            in a manner that replaces the original work.
                        </p>

                        <h2 className="text-xl font-semibold text-gray-800 dark:text-slate-100 mt-6 mb-2">
                            Images & Media Usage
                        </h2>
                        <p>
                            Some images displayed in the app may be publicly available photos, licensed assets,
                            or media sourced from APIs under permitted usage terms. These images remain the property
                            of their copyright holders.
                        </p>

                        <h2 className="text-xl font-semibold text-gray-800 dark:text-slate-100 mt-6 mb-2">
                            No Affiliation With Publishers
                        </h2>
                        <p>
                            BrieflyCA is an independent platform. We are not affiliated with, endorsed by,
                            or partnered with any Canadian news publisher, broadcaster, or media outlet.
                        </p>

                        <h2 className="text-xl font-semibold text-gray-800 dark:text-slate-100 mt-6 mb-2">
                            Takedown & Copyright Requests (DMCA / Fair Dealing)
                        </h2>
                        <p>
                            If you believe that any content on BrieflyCA violates your copyright, falls outside of
                            fair dealing/fair use protections, or you would like us to remove a specific summary,
                            image, or reference, please contact us directly at:
                        </p>

                        <p>
                            📧{" "}
                            <a
                                href="mailto:info@askstudios.ca"
                                className="text-red-600 dark:text-red-400 hover:underline"
                            >
                                info@askstudios.ca
                            </a>
                        </p>

                        <p>
                            Please include:
                        </p>
                        <ul className="list-disc pl-5 space-y-1">
                            <li>Identification of the copyrighted work</li>
                            <li>Links or screenshots of the material in question</li>
                            <li>Your contact details</li>
                            <li>A statement confirming that you are the rights holder or authorized to act on their behalf</li>
                        </ul>

                        <p>
                            We will review all requests promptly and remove or modify content where appropriate.
                        </p>

                        <h2 className="text-xl font-semibold text-gray-800 dark:text-slate-100 mt-6 mb-2">
                            Commitment to Publishers
                        </h2>
                        <p>
                            Our goal is to support Canadian journalism by helping users discover news—not replace
                            or compete with original reporting. We encourage all users to read the full articles
                            on publisher websites to support the work of journalists and news organizations.
                        </p>

                    </div>
                </article>
            </div>
        </div>
    );
};

export default CopyrightNote;
